import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import InformationalPopover from 'chameleon/ui-stack/ui/popovers/informational-popover';
import ChartLegend from '../ChartLegend/ChartLegend';

import './DonutChart.scss';

const colors = {
  green: '#6cc449',
  'light-green': '#bff5aa',
  'light-yellow': '#feed7f',
  yellow: '#fedb00',
};

class DonutChart extends Component {
  D = 264;
  R = this.D / 2;
  U = 2 * Math.PI;
  C = this.U * this.R;
  strokeWidth = this.R / 2.5;
  divider = 0.2;
  decimals = 0;

  recalculateParams(diameter, thickness) {
    this.D = diameter;
    this.R = this.D / 2;
    this.U = 2 * Math.PI;
    this.C = this.U * this.R;
    this.strokeWidth = thickness || this.strokeWidth;
  }

  getSegment(offset = (25 * this.C) / 100) {
    const divider = (this.divider * this.C) / 100;
    let offsetAdjust = offset - divider / 2;
    return (size) => {
      const diff = this.C - size;
      const data = {
        strokeDasharray: `${size - divider} ${diff + divider}`,
        strokeDashoffset: offsetAdjust,
      };
      offsetAdjust -= size;
      return data;
    };
  }

  getRay(offset = -25) {
    let offsetAdjust = offset;
    return (size) => {
      const mid = offsetAdjust + size / 2;
      const rad = (mid * this.U) / 100;
      const data = {
        x: Math.cos(rad) * this.R,
        y: Math.sin(rad) * this.R,
      };
      offsetAdjust += size;
      return data;
    };
  }

  render() {
    const {
      data,
      children,
      diameter,
      thickness,
      showLegend,
      chartTitle,
      renderPopover,
    } = this.props;
    if (diameter) {
      this.recalculateParams(diameter, thickness);
    }
    const mid = this.R + this.strokeWidth / 2;
    const circleDefaults = {
      cx: mid,
      cy: mid,
      r: this.R,
      fill: 'transparent',
      strokeWidth: this.strokeWidth,
    };
    const nextSeg = this.getSegment();
    const nextRay = this.getRay();
    return (
      <Fragment>
        <div className="chart">
          {chartTitle && (
            <div className="chart-title">
              {chartTitle}
              {renderPopover && (
                <InformationalPopover
                  icon={renderPopover.icon}
                  message={renderPopover.message}
                />
              )}
            </div>
          )}
          <div className="chart-main">
            <svg
              viewBox={`0 0 ${mid * 2} ${mid * 2}`}
              width={mid * 2}
              height={mid * 2}
            >
              <circle
                className="chart-ring"
                stroke="#FFFFFF"
                {...circleDefaults}
              />
              <g>
                {data.map(
                  ({ value, color }, i) =>
                    value && (
                      <circle
                        className="chart-segment"
                        stroke={colors[color]}
                        {...circleDefaults}
                        {...nextSeg((value * this.C) / 100)}
                        key={`chart-segment-${i}`}
                      />
                    ),
                )}
                {data.map(
                  ({ value }, i) =>
                    value && (
                      <text
                        className="chart-segment-label"
                        dx={`${mid}px`}
                        dy={`${mid}px`}
                        {...nextRay(value)}
                        tabIndex="-1"
                        key={`chart-segment-label-${i}`}
                      >
                        {parseFloat(value).toFixed(this.decimals)}%
                      </text>
                    ),
                )}
              </g>
            </svg>
            <div className="chart-center">{children}</div>
          </div>

          {showLegend && <ChartLegend data={data} />}
        </div>
      </Fragment>
    );
  }
}

DonutChart.propTypes = {
  enablePopups: PropTypes.bool,
  showLegend: PropTypes.bool,
};

DonutChart.defaultProps = {
  enablePopups: false,
  showLegend: true,
};

export default DonutChart;
